import moment from 'moment'
import type { AppointmentServiceRequest } from 'api/api.types'
import { APIService } from 'api/APIService'
import { store } from 'src/store'
import { getActiveServicesIndex } from 'src/store/data.store'
import {
  getIsAuthenticated,
  getSelectedCityId,
  setAuthToken,
  setProfile,
  setSelectedCityId,
  signOut,
} from 'src/store/app.state.store'
import {
  getAppointmentDate,
  getAppointmentSalon,
  getAppointmentSalonId,
  getAppointmentServices,
  initAppointment,
  setAppointmentAvailableTime,
  setAppointmentAvailableTimeLoading,
  setAppointmentDate,
  setAppointmentSalon,
  setAppointmentServices,
  type AppointmentState,
} from 'src/store/appointment.store'
import { refreshPage } from 'utils/initUtils'
import { Storage } from 'utils/storage'
import { StoreObserver } from './StoreObserver'

const TOKEN_REFRESH_INTERVAL = 60 * 60 * 1000
const SAVED_STATE_TTL = 24 * 60 * 60 * 1000
/** Storage key of the saved state; its format is kept from the JS site so logins survive the update */
const SAVED_STATE_KEY = 'state'

type SavedState = {
  appointment?: AppointmentState,
  profile?: { token: string | null },
  data?: { selectedCityId: number | null },
}

let refreshIntervalId: ReturnType<typeof setInterval> | undefined

const refreshToken = async () => {
  try {
    store.dispatch(setAuthToken(await APIService.auth.refreshToken()))
  } catch {
    store.dispatch(signOut())
  }
}

const onAuthChange = async (isAuthenticated: boolean) => {
  if (!isAuthenticated) {
    clearInterval(refreshIntervalId)
    return
  }
  await refreshToken()
  store.dispatch(setProfile(await APIService.profile.fetchProfile()))
  refreshIntervalId = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL)
}

const restoreSavedState = () => {
  const savedState = Storage.getItem(SAVED_STATE_KEY) as SavedState | null
  if (!savedState) {
    return
  }
  if (savedState.appointment) {
    store.dispatch(initAppointment(savedState.appointment))
  }
  if (savedState.profile) {
    store.dispatch(setAuthToken(savedState.profile.token))
  }
  if (savedState.data) {
    store.dispatch(setSelectedCityId(savedState.data.selectedCityId))
  }
}

const loadAvailableTime = async (
  salonId: number | null,
  services: Record<number, AppointmentServiceRequest[]>,
  date: string | null,
) => {
  const hasServices = Object.values(services ?? {}).some((clientServices) => clientServices.length > 0)
  if (salonId === null || !hasServices || date === null) {
    store.dispatch(setAppointmentAvailableTime(null))
    return
  }
  store.dispatch(setAppointmentAvailableTimeLoading(true))
  const time = await APIService.appointment.fetchFreeTime(salonId, services, date)
  store.dispatch(setAppointmentAvailableTime(time))
  store.dispatch(setAppointmentAvailableTimeLoading(false))
}

/** Browser-only reactions to store changes; page loading on navigation lives in AppContent */
export const createObservers = () => {
  restoreSavedState()

  const observer = new StoreObserver(store)

  // The booking date is never in the past
  observer.listen([getAppointmentDate], (date) => {
    if (!date || moment(date).isBefore(moment(), 'day')) {
      store.dispatch(setAppointmentDate(moment().format('YYYY-MM-DD')))
    }
  }, true)

  // Prices and salons depend on the city, so the page is reloaded
  observer.listen([getSelectedCityId], async (cityId) => {
    if (cityId) {
      await refreshPage(store)
    }
  })

  observer.listen([getAppointmentSalonId, getAppointmentServices, getAppointmentDate], loadAvailableTime, true)

  // Choosing a salon switches to its city
  observer.listen([getAppointmentSalon], (salon) => {
    if (salon) {
      store.dispatch(setSelectedCityId(salon.cityId))
    }
  })

  // Switching the city drops a booking salon of another city
  observer.listen([getSelectedCityId], (cityId) => {
    const appointmentSalon = getAppointmentSalon(store.getState())
    if (appointmentSalon && appointmentSalon.cityId !== cityId) {
      store.dispatch(setAppointmentSalon(null))
    }
  })

  // Drop booked services that the city does not offer
  observer.listen([getActiveServicesIndex, getAppointmentServices], (servicesIndex, appointmentServices) => {
    if (!servicesIndex || !appointmentServices) {
      return
    }
    Object.entries(appointmentServices).forEach(([clientIndex, clientServices]) => {
      const filteredServices = clientServices.filter((s) => servicesIndex[s.id] !== undefined)
      if (filteredServices.length !== clientServices.length) {
        store.dispatch(setAppointmentServices({ clientIndex: Number(clientIndex), services: filteredServices }))
      }
    })
  })

  observer.listen([getIsAuthenticated], onAuthChange, true)

  store.subscribe(() => {
    const { appointment, appState } = store.getState()
    Storage.setItem(SAVED_STATE_KEY, {
      appointment,
      profile: {
        token: appState.authToken,
        profile: appState.profile,
      },
      data: {
        selectedCityId: appState.selectedCityId,
      },
    }, Date.now() + SAVED_STATE_TTL)
  })
}
